import React, { Component } from "react";
import { ContentWrapper, Icon, Table } from "components";
import { Row, Col, Input, notification, DatePicker, Button, Select, Drawer, Switch, Popconfirm, Modal } from "antd";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import Strings from "utils/strings";
import { push } from "connected-react-router";
import moment from "moment";
import { LANGUAGES, translate } from "utils/utils";
import "./styles.scss";

// Editable input component that maintains local state to prevent losing focus
class EditableNumberInput extends React.Component<any, any> {
	constructor(props: any) {
		super(props);
		this.state = {
			value: props.value != null ? props.value : "",
			isFocused: false
		};
	}

	componentDidUpdate(prevProps: any) {
		// Only update local state if the prop value changed and we're not focused
		if (prevProps.value !== this.props.value && !this.state.isFocused) {
			this.setState({ value: this.props.value != null ? this.props.value : "" });
		}
	}

	render() {
		const { onChange, onBlur, placeholder, style, step, ...restProps } = this.props;
		const { value } = this.state;

		return (
			<Input
				{...restProps}
				type="number"
				step={step || "0.01"}
				value={value}
				placeholder={placeholder}
				style={style}
				onFocus={() => {
					this.setState({ isFocused: true });
				}}
				onChange={(e: any) => {
					const inputValue = e.target.value;
					this.setState({ value: inputValue });
					// Don't call onChange immediately to prevent re-render
				}}
				onBlur={(e: any) => {
					const inputValue = e.target.value;
					this.setState({ isFocused: false });
					if (onBlur) {
						onBlur(e);
					}
					// Sync with parent on blur
					if (onChange && inputValue !== this.props.value) {
						onChange(inputValue);
					}
				}}
			/>
		);
	}
}

// Editable text input that keeps local state while typing to avoid losing focus on re-render
class EditableTextInput extends React.Component<any, any> {
	constructor(props: any) {
		super(props);
		this.state = {
			value: props.value != null ? props.value : "",
			isFocused: false
		};
	}

	componentDidUpdate(prevProps: any) {
		if (prevProps.value !== this.props.value && !this.state.isFocused) {
			this.setState({ value: this.props.value != null ? this.props.value : "" });
		}
	}

	render() {
		const { onChange, onBlur, placeholder, ...restProps } = this.props;
		const { value } = this.state;

		return (
			<Input
				{...restProps}
				value={value}
				placeholder={placeholder}
				onFocus={() => this.setState({ isFocused: true })}
				onChange={(e: any) => this.setState({ value: e.target.value })}
				onBlur={(e: any) => {
					const inputValue = e.target.value;
					this.setState({ isFocused: false });
					if (onBlur) onBlur(e);
					if (onChange && inputValue !== this.props.value) {
						onChange(inputValue);
					}
				}}
			/>
		);
	}
}

class WorkshopInterventionDetail extends Component<any, any> {
	constructor(props: any) {
		super(props);

		this.state = {
			intervention: null,
			hasUnsavedFields: false,
			isNew: props.match?.params?.id === "new",
			date: null,
			vehicle: null,
			scheduledKm: null,
			scheduledDate: null,
			scheduleNextReview: false,
			km: null,
			nextReviewDate: null,
			nextReviewKm: null,
			notes: "",
			items: [],
			parts: [],
			vehicles: [],
			total: 0,
			showPartDrawer: false,
			tempPart: null,
			partIndex: undefined,
			language: "pt",
			suppliersForParts: [],
			showStockModal: false,
			stockModalData: null,
			partsWithZeroStockWarned: []
		};
	}

	componentDidMount(): void {
		delayedDispatch(
			setBreadcrumb(() => {
				return {
					locations: [
						{
							text: Strings.workshop.interventions,
							route: "/workshop/interventions",
							icon: "tools"
						},
						{
							text: this.state.isNew
								? Strings.workshop.newIntervention
								: Strings.workshop.intervention
						}
					],
					actions: [
						{
							type: "button",
							text: Strings.generic.save,
							onClick: () => this.submitIntervention(),
							disabled: !this.state.hasUnsavedFields,
							className: this.state.hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							isSave: true
						}
					]
				};
			})
		);

		this.getData();
		this.getParts();
		this.getVehicles();
		this.getSuppliersForParts();
	}

	async getSuppliersForParts() {
		try {
			const response = await API.get({
				url: Endpoints.uriSuppliers()
			});

			if (response.ok) {
				this.setState({ suppliersForParts: response.data?.results?.supplier || [] });
			}
		} catch (err) {
			console.log("API Error", err);
		}
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;

		dispatch(updateCrumb());
	}

	async getData() {
		const { isNew } = this.state;
		const { dispatch, match } = this.props;

		if (isNew) {
			return dispatch(setTitle(Strings.workshop.newIntervention));
		}

		dispatch(setLoader(true));

		try {
			const response = await API.get({
				url: Endpoints.uriWorkshopInterventions(match?.params?.id)
			});

			if (response.ok) {
				const intervention = response.data?.results?.intervention;
				dispatch(setTitle(Strings.workshop.intervention));
				this.setState({
					intervention,
					date: intervention?.date ? moment(intervention.date) : null,
					vehicle: intervention?.vehicle?._id || null,
					scheduledKm: intervention?.scheduledKm || null,
					scheduledDate: intervention?.scheduledDate ? moment(intervention.scheduledDate) : null,
					scheduleNextReview: Boolean(intervention?.nextReviewDate || intervention?.nextReviewKm),
					km: intervention?.km || null,
					nextReviewDate: intervention?.nextReviewDate ? moment(intervention.nextReviewDate) : null,
					nextReviewKm: intervention?.nextReviewKm || null,
					notes: intervention?.notes || "",
					items: (intervention?.items || []).map((item: any) => ({
						...item,
						type: item.type || "part",
						part: item.part?._id ? String(item.part._id) : (item.part ? String(item.part) : null),
						description: item.description || ""
					})),
					total: intervention?.total || 0
				});
			}
		} catch (err) {
			console.log("API Error", err);
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async getParts() {
		try {
			const response = await API.get({
				url: Endpoints.uriWorkshopParts()
			});

			if (response.ok) {
				this.setState({ parts: response.data?.results?.part || [] });
			}
		} catch (err) {
			console.log("API Error", err);
		}
	}

	async getVehicles() {
		try {
			const response = await API.get({
				url: Endpoints.uriVehicles()
			});

			if (response.ok) {
				this.setState({ vehicles: response.data?.results?.vehicle || [] });
			}
		} catch (err) {
			console.log("API Error", err);
		}
	}

	calculateTotal() {
		const { items } = this.state;
		let total = 0;
		items.forEach((item: any) => {
			const quantity = item.quantity != null ? item.quantity : 0;
			const unitPrice = item.unitPrice != null ? item.unitPrice : 0;
			if (quantity && unitPrice) {
				total += quantity * unitPrice;
			}
		});
		this.setState({ total, hasUnsavedFields: true });
	}

		addItem() {
		const { items } = this.state;
		this.setState(
			{
				items: [
					...items,
					{
						type: "part",
						part: null,
						description: "",
						quantity: 1,
						unitPrice: 0,
						total: 0
					}
				],
				hasUnsavedFields: true
			},
			() => this.calculateTotal()
		);
	}

	removeItem(index: number) {
		const { items } = this.state;
		const newItems = [...items];
		newItems.splice(index, 1);
		this.setState({ items: newItems, hasUnsavedFields: true }, () => this.calculateTotal());
	}

	checkStock(partId: string, quantity: number | null, isPartSelection: boolean = false) {
		const { parts, partsWithZeroStockWarned } = this.state;
		const selectedPart = parts.find((p: any) => String(p._id) === String(partId));
		
		if (!selectedPart) return;
		
		const stock = selectedPart.quantity || 0;
		const partNumber = selectedPart.partNumber || "";
		const partName = translate(selectedPart.name) || "-";
		
		// Check if stock is <= 0
		if (stock <= 0) {
			// Only show if it's a part selection or if we haven't warned about this part yet
			if (isPartSelection || !partsWithZeroStockWarned.includes(partId)) {
				const newWarnedList = isPartSelection && !partsWithZeroStockWarned.includes(partId) 
					? [...partsWithZeroStockWarned, partId]
					: partsWithZeroStockWarned;
				this.setState({
					showStockModal: true,
					stockModalData: {
						partNumber,
						partName,
						stock,
						message: Strings.workshop.insufficientStock || "Não existe stock disponível para esta peça."
					},
					partsWithZeroStockWarned: newWarnedList
				});
			}
			return;
		}
		
		// Check if quantity exceeds stock (only if stock > 0)
		if (quantity != null && quantity > stock) {
			this.setState({
				showStockModal: true,
				stockModalData: {
					partNumber,
					partName,
					stock,
					requestedQuantity: quantity,
					message: `${Strings.workshop.insufficientStock || "Stock insuficiente"}. Stock disponível: ${stock}`
				}
			});
		}
	}

	updateItem(index: number, field: string, value: any) {
		const { items, parts } = this.state;
		// Ensure part ID is always a string for Select component compatibility
		const processedValue = field === "part" && value ? String(value) : value;
		
		// Create a new array to ensure React detects the change
		const newItems = [...items];
		newItems[index] = {
			...newItems[index],
			[field]: processedValue
		};
		
		// When switching type, clear the other field
		if (field === "type") {
			if (processedValue === "service") {
				newItems[index].part = null;
			} else {
				newItems[index].description = "";
			}
		}
		
		// If part is selected, get its averageCost and update unitPrice
		if (field === "part" && processedValue) {
			const selectedPart = parts.find((p: any) => String(p._id) === String(processedValue));
			if (selectedPart) {
				newItems[index].unitPrice = selectedPart.averageCost || 0;
				// Check stock when part is selected (check current quantity if exists)
				const currentQuantity = newItems[index].quantity != null ? parseFloat(newItems[index].quantity) : null;
				this.checkStock(processedValue, currentQuantity, true);
			}
		}
		
		if (field === "quantity" || field === "part" || field === "unitPrice") {
			const quantity = newItems[index].quantity != null ? parseFloat(newItems[index].quantity) || 0 : 0;
			const unitPrice = newItems[index].unitPrice != null ? parseFloat(newItems[index].unitPrice) || 0 : 0;
			newItems[index].total = quantity * unitPrice;
			
			// Check stock when quantity is changed
			if (field === "quantity" && newItems[index].part) {
				const quantityValue = newItems[index].quantity != null ? parseFloat(newItems[index].quantity) : null;
				this.checkStock(newItems[index].part, quantityValue);
			}
		}
		
		this.setState({ items: newItems, hasUnsavedFields: true }, () => this.calculateTotal());
	}

	async submitIntervention() {
		const { date, vehicle, scheduledKm, scheduledDate, scheduleNextReview, km, nextReviewDate, nextReviewKm, notes, items, isNew } = this.state;
		const { dispatch, match } = this.props;

		if (!date || !items || items.length === 0) {
			return notification.warn({
				message: Strings.workshop.intervention,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		// Validate items
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (item.type === "part") {
				if (!item.part || item.quantity == null || item.quantity === "" || !item.unitPrice) {
					return notification.warn({
						message: Strings.workshop.intervention,
						description: Strings.errors.invalidFields,
						placement: "bottomRight",
						duration: 5
					});
				}
			} else {
				if (!(item.description || "").trim() || item.quantity == null || item.quantity === "" || item.unitPrice == null) {
					return notification.warn({
						message: Strings.workshop.intervention,
						description: Strings.errors.invalidFields,
						placement: "bottomRight",
						duration: 5
					});
				}
			}
		}

		dispatch(setLoader(true));

		try {
			const request = !isNew ? API.put : API.post;
			const response = await request({
				url: Endpoints.uriWorkshopInterventions((!isNew && match?.params?.id) || ""),
				data: {
					date: date.format("YYYY-MM-DD"),
					vehicle: vehicle || undefined,
					scheduledKm: scheduledKm || null,
					scheduledDate: scheduledDate ? scheduledDate.format("YYYY-MM-DD") : null,
					km: km || null,
					nextReviewDate: scheduleNextReview && nextReviewDate ? nextReviewDate.format("YYYY-MM-DD") : null,
					nextReviewKm: scheduleNextReview && nextReviewKm ? nextReviewKm : null,
					notes: notes || undefined,
					items: items.map((item: any) => ({
						type: item.type || "part",
						part: item.type === "part" ? item.part : undefined,
						description: item.type === "service" ? (item.description || "").trim() : undefined,
						quantity: item.quantity,
						unitPrice: item.unitPrice != null ? item.unitPrice : 0,
						total: item.total || 0
					})),
					total: this.state.total
				}
			});

			if (response.ok && response.data.results?.intervention) {
				if (isNew) {
					return dispatch(push("/workshop/interventions"));
				}

				const intervention = response.data.results.intervention;
				this.setState({
					hasUnsavedFields: false,
					intervention,
					date: intervention?.date ? moment(intervention.date) : null,
					vehicle: intervention?.vehicle?._id || null,
					scheduledKm: intervention?.scheduledKm || null,
					scheduledDate: intervention?.scheduledDate ? moment(intervention.scheduledDate) : null,
					scheduleNextReview: Boolean(intervention?.nextReviewDate || intervention?.nextReviewKm),
					km: intervention?.km || null,
					nextReviewDate: intervention?.nextReviewDate ? moment(intervention.nextReviewDate) : null,
					nextReviewKm: intervention?.nextReviewKm || null,
					notes: intervention?.notes || "",
					items: (intervention?.items || []).map((item: any) => ({
						...item,
						type: item.type || "part",
						part: item.part?._id ? String(item.part._id) : (item.part ? String(item.part) : null),
						description: item.description || ""
					})),
					total: intervention?.total || 0
				});

				notification.success({
					message: Strings.workshop.intervention,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderInfo() {
		const { date, vehicle, scheduledKm, scheduledDate, scheduleNextReview, km, nextReviewDate, nextReviewKm, notes, vehicles } = this.state;

		return (
			<ContentWrapper extraStyle={{ padding: 20 }}>
				<div className="WorksheetTitle" style={{ paddingBottom: 30 }}>
					<Icon name="inspection" />
					<p>{Strings.worksheets.generalInfo}</p>
				</div>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="date" className="InputLabel --label-required">
							{Strings.workshop.date}
						</label>
						<DatePicker
							id="date"
							style={{ width: "100%" }}
							value={date}
							onChange={(date: any) => {
								this.setState({ date, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="vehicle" className="InputLabel">
							{Strings.workshop.vehicle}
						</label>
						<Select
							id="vehicle"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.selectVehicle}
							showSearch
							allowClear
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={vehicle || null}
							onChange={(value: any) => {
								if (!value) {
									// When vehicle is cleared, reset related fields
									this.setState({
										vehicle: null,
										scheduledKm: null,
										scheduledDate: null,
										scheduleNextReview: false,
										km: null,
										nextReviewDate: null,
										nextReviewKm: null,
										hasUnsavedFields: true
									});
								} else {
									// Find the selected vehicle to get its nextReviewKm and nextReviewDate
									const selectedVehicle = vehicles.find((v: any) => v._id === value);
									const scheduledKm = selectedVehicle?.nextReviewKm || null;
									const scheduledDate = selectedVehicle?.nextReviewDate ? moment(selectedVehicle.nextReviewDate) : null;
									
									this.setState({ 
										vehicle: value, 
										scheduledKm,
										scheduledDate,
										hasUnsavedFields: true 
									});
								}
							}}
						>
							{vehicles
								.filter((v: any) => v.isActive !== false)
								.map((vehicle: any) => (
									<Select.Option key={vehicle._id} value={vehicle._id}>
										{vehicle.licensePlate || "-"}
									</Select.Option>
								))}
						</Select>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="scheduledKm" className="InputLabel">
							{Strings.workshop.scheduledKm}
						</label>
						<Input
							id="scheduledKm"
							type="number"
							value={scheduledKm != null ? scheduledKm : ""}
							placeholder={Strings.workshop.scheduledKm}
							disabled
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="scheduledDate" className="InputLabel">
							{Strings.workshop.scheduledDate}
						</label>
						<DatePicker
							id="scheduledDate"
							disabled
							style={{ width: "100%" }}
							value={scheduledDate}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<div
							className={`General_ColorFul_Switch ${
								scheduleNextReview ? "__active" : ""
							}`}
							style={{ marginTop: 30 }}
						>
							<span>{Strings.workshop.scheduleNextReview}</span>
							<Switch
								className={`Switch ${
									scheduleNextReview ? "__active" : ""
								}`}
								checked={scheduleNextReview || false}
								disabled={!vehicle}
								size="small"
								onChange={(value: any) => {
									this.setState({ scheduleNextReview: !scheduleNextReview, hasUnsavedFields: true });
								}}
							/>
						</div>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="km" className="InputLabel">
							{Strings.workshop.currentKm}
						</label>
						<Input
							id="km"
							type="number"
							value={km != null ? km : ""}
							placeholder={Strings.workshop.currentKm}
							disabled={!vehicle}
							onChange={(e: any) => {
								const value = e.target.value === "" ? null : parseFloat(e.target.value);
								this.setState({ km: value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					{scheduleNextReview && vehicle && <Col xs={24} md={12} lg={6}>
						<label htmlFor="nextReviewKm" className="InputLabel">
							{Strings.workshop.nextReviewKm}
						</label>
						<Input
							id="nextReviewKm"
							type="number"
							value={nextReviewKm != null ? nextReviewKm : ""}
							placeholder={Strings.workshop.nextReviewKm}
							onChange={(e: any) => {
								const value = e.target.value === "" ? null : parseFloat(e.target.value);
								this.setState({ nextReviewKm: value, hasUnsavedFields: true });
							}}
						/>
					</Col>}
					{scheduleNextReview && vehicle && <Col xs={24} md={12} lg={6}>
						<label htmlFor="nextReviewDate" className="InputLabel">
							{Strings.workshop.nextReviewDate}
						</label>
						<DatePicker
							id="nextReviewDate"
							style={{ width: "100%" }}
							value={nextReviewDate}
							onChange={(date: any) => {
								this.setState({ nextReviewDate: date, hasUnsavedFields: true });
							}}
						/>
					</Col>}
					<Col xs={24}>
						<label htmlFor="notes" className="InputLabel">
							{Strings.fields.notes}
						</label>
						<Input.TextArea
							id="notes"
							placeholder={Strings.fields.notes}
							value={notes || ""}
							rows={4}
							onChange={(event: any) => {
								this.setState({ notes: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
				</Row>
			</ContentWrapper>
		);
	}

	renderItems() {
		const { items, total, parts } = this.state;

		return (
			<ContentWrapper>
				<Table
					title={{
						icon: "inspection",
						title: Strings.workshop.items
					}}
					data={items.map((item: any, index: number) => ({ ...item, _index: index }))}
					columns={[
						{
							Header: Strings.workshop.type,
							id: "type",
							accessor: (row: any) => row.type || "part",
							Cell: ({ row }: any) => {
								const index = row.original._index;
								const type = row.original.type || "part";
								return (
									<Select
										style={{ width: "100%" }}
										value={type}
										onChange={(val) => this.updateItem(index, "type", val)}
									>
										<Select.Option value="part">{Strings.workshop.part}</Select.Option>
										<Select.Option value="service">{Strings.workshop.service}</Select.Option>
									</Select>
								);
							}
						},
						{
							Header: Strings.workshop.part + " / " + Strings.workshop.description,
							id: "partOrDescription",
							width: 300,
							accessor: (row: any) => {
								if (row.type === "service") return row.description || "";
								const partId = row.part?._id || row.part || null;
								return partId ? String(partId) : "";
							},
							Cell: ({ row }: any) => {
								const item = row.original;
								const index = item._index;
								const type = item.type || "part";
								if (type === "service") {
									return (
										<EditableTextInput
											value={item.description || ""}
											placeholder={Strings.workshop.description}
											onChange={(value: string) => this.updateItem(index, "description", value)}
										/>
									);
								}
								const partId = item.part?._id || item.part || null;
								const partIdString = partId ? String(partId) : undefined;
								return (
									<Select
										style={{ width: "100%" }}
										value={partIdString}
										placeholder={Strings.workshop.selectPart}
										showSearch
										optionLabelProp="label"
										dropdownStyle={{ minWidth: "400px" }}
										filterOption={(input: any, option: any) => {
											const searchText = input.toLowerCase();
											const partNumber = option.props?.partNumber?.toLowerCase() || "";
											const partName = option.props?.partName?.toLowerCase() || "";
											return partNumber.indexOf(searchText) >= 0 || partName.indexOf(searchText) >= 0;
										}}
										onChange={(val) => this.updateItem(index, "part", val)}
										dropdownRender={(menu: any) => (
											<div>
												{menu}
												<div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
													<Button
														type="link"
														style={{ width: "100%" }}
														onClick={() => this.setState({ showPartDrawer: true, tempPart: {}, partIndex: index })}
													>
														+ {Strings.workshop.newPart}
													</Button>
												</div>
											</div>
										)}
									>
										{parts
											.filter((p: any) => p.isActive !== false)
											.map((part: any) => {
												const partName = translate(part.name) || "-";
												const partNumber = part.partNumber || "";
												return (
													<Select.Option 
														key={String(part._id)} 
														value={String(part._id)}
														label={partName}
														partNumber={partNumber}
														partName={partName}
													>
														{partNumber ? `${partNumber} - ${partName}` : partName}
													</Select.Option>
												);
											})}
									</Select>
								);
							}
						},
						{
							Header: Strings.workshop.unit,
							id: "unit",
							accessor: (row: any) => {
								if (row.type === "service") return "-";
								const partId = row.part || null;
								const selectedPart = parts.find((p: any) => String(p._id) === String(partId));
								return selectedPart?.unit || "-";
							}
						},
						{
							Header: Strings.workshop.quantity,
							id: "quantity",
							align: "right",
							accessor: (row: any) => row.quantity != null ? row.quantity : "",
							Cell: ({ row }: any) => {
								const quantity = row.original.quantity != null ? row.original.quantity : "";
								const index = row.original._index;
								return (
									<EditableNumberInput
										min={0}
										step="0.01"
										value={quantity}
										placeholder="0"
										style={{ textAlign: "right" }}
										onChange={(inputValue: any) => {
											if (inputValue === "" || inputValue === null) {
												this.updateItem(index, "quantity", null);
											} else {
												const numValue = parseFloat(inputValue);
												if (!isNaN(numValue)) {
													this.updateItem(index, "quantity", numValue);
												}
											}
										}}
									/>
								);
							}
						},
						{
							Header: Strings.workshop.unitPrice,
							id: "unitPrice",
							align: "right",
							accessor: (row: any) => {
								const unitPrice = row.unitPrice != null ? row.unitPrice : 0;
								return `${unitPrice.toFixed(2)} €`;
							},
							Cell: ({ row }: any) => {
								const item = row.original;
								const unitPrice = item.unitPrice != null ? item.unitPrice : 0;
								const index = item._index;
								const isService = item.type === "service";
								if (isService) {
									return (
										<EditableNumberInput
											min={0}
											step="0.01"
											value={unitPrice}
											placeholder="0.00"
											style={{ textAlign: "right" }}
											onChange={(inputValue: any) => {
												if (inputValue === "" || inputValue === null) {
													this.updateItem(index, "unitPrice", 0);
												} else {
													const numValue = parseFloat(inputValue);
													if (!isNaN(numValue)) {
														this.updateItem(index, "unitPrice", parseFloat(numValue.toFixed(2)));
													}
												}
											}}
										/>
									);
								}
								return <span style={{ textAlign: "right", display: "block" }}>{(unitPrice || 0).toFixed(2)} €</span>;
							}
						},
						{
							Header: Strings.workshop.total,
							id: "total",
							align: "right",
							accessor: (row: any) => `${(row.total || 0).toFixed(2)} €`
						},
						{
							Header: Strings.generic.actions,
							id: "actions",
							align: "right",
							accessor: () => "",
							Cell: ({ row }: any) => {
								const index = row.original._index;
								return (
									<Popconfirm
										title="Tem a certeza?"
										okText="Remover"
										cancelText="Cancelar"
										okType="danger"
										placement="topRight"
										onConfirm={() => this.removeItem(index)}
									>
										<div className="ActionOption" style={{ cursor: "pointer", display: "inline-block" }}>
											<Icon name="trash" />
										</div>
									</Popconfirm>
								);
							}
						}
					]}
					fullPage
					paginated={false}
					add={{
						onClick: () => this.addItem()
					}}
				/>
				<div style={{ marginTop: 20, textAlign: "right" }}>
					<label className="InputLabel" style={{ fontSize: 18, fontWeight: 700 }}>
						{Strings.workshop.total}: {total.toFixed(2)} €
					</label>
				</div>
			</ContentWrapper>
		);
	}

	renderStockModal() {
		const { showStockModal, stockModalData } = this.state;

		if (!showStockModal || !stockModalData) return null;

		return (
			<Modal
				centered
				width="500px"
				visible={!!showStockModal}
				title="Aviso de Stock"
				onOk={() => {
					this.setState({ showStockModal: false, stockModalData: null });
				}}
				okText="OK"
				footer={[
					<Button key="ok" type="primary" onClick={() => {
						this.setState({ showStockModal: false, stockModalData: null });
					}}>
						OK
					</Button>
				]}
			>
				<div style={{ paddingBottom: 20, paddingTop: 20 }}>
					<div style={{ marginBottom: 10 }}>
						<strong>Código:</strong> {stockModalData.partNumber}
					</div>
					<div style={{ marginBottom: 10 }}>
						<strong>Descrição:</strong> {stockModalData.partName}
					</div>
					<div style={{ marginBottom: 10, color: "#ff4d4f" }}>
						{stockModalData.message}
					</div>
					{stockModalData.requestedQuantity != null && (
						<div style={{ marginTop: 10 }}>
							<strong>Quantidade solicitada:</strong> {stockModalData.requestedQuantity}
						</div>
					)}
					{stockModalData.stock != null && (
						<div>
							<strong>Stock disponível:</strong> {stockModalData.stock}
						</div>
					)}
				</div>
			</Modal>
		);
	}

	render(): JSX.Element {
		const { isNew } = this.state;

		return (
			<React.Fragment>
				<Helmet>
					<title>
						{Strings.workshop.intervention} -{" "}
						{isNew ? Strings.workshop.newIntervention : Strings.workshop.intervention}
					</title>
					<meta name="description" content="Description of Workshop Intervention" />
				</Helmet>
				<div className="WorksheetScreen" style={{ paddingBottom: 80 }}>
					{this.renderInfo()}
					{this.renderItems()}
				</div>
				{this.renderPartDrawer()}
				{this.renderStockModal()}
			</React.Fragment>
		);
	}

	async submitPart() {
		const { tempPart, partIndex } = this.state;
		const { dispatch } = this.props;

		if (!tempPart?.partNumber?.trim() || !translate(tempPart?.name)) {
			return notification.warn({
				message: Strings.workshop.part,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const response = await API.post({
				url: Endpoints.uriWorkshopParts(),
				data: {
					partNumber: tempPart.partNumber?.toUpperCase() || "",
					name: tempPart.name,
					instructions: tempPart.instructions || {},
					unit: tempPart.unit || "un",
					preferredSupplier: tempPart.preferredSupplier || undefined
				}
			});

			if (response.ok && response.data.results?.part) {
				const newPart = response.data.results.part;
				await this.getParts();
				
				if (partIndex !== undefined && newPart?._id) {
					this.updateItem(partIndex, "part", String(newPart._id));
				}
				this.setState({
					showPartDrawer: false,
					tempPart: null,
					partIndex: undefined,
					hasUnsavedFields: true
				});

				notification.success({
					message: Strings.workshop.part,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderPartDrawer() {
		const { showPartDrawer, tempPart, language, suppliersForParts } = this.state;

		const unitOptions = [
			{ value: 'un', label: 'Unidade (un)' },
			{ value: 'l', label: 'Litro (l)' },
			{ value: 'kg', label: 'Quilograma (kg)' },
			{ value: 'm', label: 'Metro (m)' },
			{ value: 'm2', label: 'Metro Quadrado (m²)' },
			{ value: 'ml', label: 'Metro Linear (ml)' }
		];

		return (
			<Drawer
				title={
					<div className="SidebarTitleContainer">
						<Icon name="tools" />
						<p>{Strings.workshop.newPart}</p>
					</div>
				}
				footer={
					<div className="SidebarFooterContainer">
						<button
							type="button"
							className="SidebarFooterButton --button-confirm"
							onClick={() => this.submitPart()}
						>
							{Strings.generic.confirm}
						</button>
						<button
							type="button"
							className="SidebarFooterButton --button-cancel"
							onClick={() => this.setState({ showPartDrawer: false, tempPart: null, partIndex: undefined })}
						>
							{Strings.generic.cancel}
						</button>
					</div>
				}
				placement="right"
				width={400}
				onClose={() => this.setState({ tempPart: null, showPartDrawer: false, partIndex: undefined })}
				visible={showPartDrawer}
			>
				<div className="DrawerLanguageContainer">
					<Select
						className="DrawerLanguageDropdown"
						optionFilterProp="children"
						onChange={(lang) => this.setState({ language: lang })}
						filterOption={(input: any, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						value={language}
					>
						{LANGUAGES.map((lang: any, index: number) => {
							return (
								<Select.Option key={`select_option_${lang}_${index}`} value={lang.value}>
									{lang.label}
								</Select.Option>
							);
						})}
					</Select>
				</div>
				<Row gutter={[0, 10]}>
					<Col xs={24}>
						<label htmlFor="part_number" className="InputLabel --label-required">
							{Strings.workshop.partNumber}
						</label>
						<Input
							id="part_number"
							value={tempPart?.partNumber || ""}
							placeholder={Strings.workshop.partNumber}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										partNumber: value
									}
								}));
							}}
							onBlur={(event: any) => {
								const value = event.target.value?.toUpperCase() || "";
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										partNumber: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_unit" className="InputLabel --label-required">
							{Strings.workshop.unit}
						</label>
						<Select
							id="part_unit"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.unit}
							value={tempPart?.unit || 'un'}
							onChange={(value: any) => {
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										unit: value
									}
								}));
							}}
						>
							{unitOptions.map((option) => (
								<Select.Option key={option.value} value={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_name" className="InputLabel --label-required">
							{Strings.workshop.name}
						</label>
						<Input
							id="part_name"
							value={tempPart?.name?.[language] || ""}
							placeholder={Strings.workshop.name}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										name: { ...state.tempPart?.name, [language]: value }
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_instructions" className="InputLabel">
							{Strings.workshop.instructions}
						</label>
						<Input.TextArea
							id="part_instructions"
							value={tempPart?.instructions?.[language] || ""}
							placeholder={Strings.workshop.instructions}
							rows={4}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										instructions: { ...state.tempPart?.instructions, [language]: value }
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_preferredSupplier" className="InputLabel">
							{Strings.workshop.preferredSupplier}
						</label>
						<Select
							id="part_preferredSupplier"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.preferredSupplier}
							showSearch
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={tempPart?.preferredSupplier || null}
							onChange={(value: any) => {
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										preferredSupplier: value
									}
								}));
							}}
							allowClear
						>
							{suppliersForParts
								.filter((s: any) => s.isActive !== false)
								.map((supplier: any) => (
									<Select.Option key={supplier._id} value={supplier._id}>
										{supplier.name || "-"}
									</Select.Option>
								))}
						</Select>
					</Col>
				</Row>
			</Drawer>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language
});

export default connect(mapStateToProps)(WorkshopInterventionDetail);

